"use client";

import React, { useEffect, useState, useRef, useId } from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, Plus, Minus, Compass, Users, Anchor, Sparkles, CircleAlert as AlertCircle, Clock, RotateCcw, Check } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface Pillar {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const pillars: Pillar[] = [{
  title: "Family Legacy",
  description: "Private estates and milestone occasions designed to endure in family memory.",
  icon: Sparkles
}, {
  title: "Generational Harmony",
  description: "The Anchor & Orbit™ method gives grandparents, adults, and kids tailored space.",
  icon: Users
}, {
  title: "Logistical Precision",
  description: "End-to-end multi-villa, maritime, and charter architecture managed with zero friction.",
  icon: Anchor
}];
export default function Consultation() {
  const [formData, setFormData] = useState<any>(null);
  const [formStatus, setFormStatus] = useState<"loading" | "ready" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string>("");

  // Form State
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [occasion, setOccasion] = useState("");
  const [travelDates, setTravelDates] = useState("");
  const [specialReqs, setSpecialReqs] = useState("");
  const [honeypotVal, setHoneypotVal] = useState("");

  // Steppers for traveler counts
  const [seniors, setSeniors] = useState(2);
  const [adults, setAdults] = useState(4);
  const [teens, setTeens] = useState(2);
  const [kids, setKids] = useState(0);
  const totalTravelers = seniors + adults + teens + kids;
  const nameInputId = useId();
  const emailInputId = useId();
  const phoneInputId = useId();
  const datesInputId = useId();
  const reqsInputId = useId();
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    const fetchFormDefinition = async () => {
      try {
        if (!client?.pluginRequest) {
          if (isMounted) {
            setFormStatus("ready");
            setFetchTimestamp(Date.now());
          }
          return;
        }
        try {
          const res = await client.pluginRequest("wvc-forms/v1/forms/discovery-consultation");
          if (isMounted) {
            setFormData(res);
            setFormStatus("ready");
            setFetchTimestamp(Date.now());
          }
        } catch (err: any) {
          if (err?.status === 404) {
            // Recover by finding the matching form
            const listRes = await client.pluginRequest("wvc-forms/v1/forms");
            const match = listRes?.items?.find((f: any) => f.key === "discovery-consultation" || f.name?.toLowerCase().includes("consultation") || f.name?.toLowerCase().includes("discovery")) || (listRes?.items?.length === 1 ? listRes.items[0] : null);
            if (match) {
              const matchedForm = await client.pluginRequest(`wvc-forms/v1/forms/${match.key}`);
              if (isMounted) {
                setFormData(matchedForm);
                setFormStatus("ready");
                setFetchTimestamp(Date.now());
              }
            } else {
              if (isMounted) {
                setFormStatus("ready");
                setFetchTimestamp(Date.now());
              }
            }
          } else {
            if (isMounted) {
              setFormStatus("ready");
              setFetchTimestamp(Date.now());
            }
          }
        }
      } catch {
        if (isMounted) {
          setFormStatus("ready");
          setFetchTimestamp(Date.now());
        }
      }
    };
    fetchFormDefinition();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setFieldErrors({});

    // Enforce spam minimum seconds check
    const minSeconds = formData?.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTime = Math.ceil(minSeconds - elapsedSeconds);
      setErrorMessage(`Please wait ${waitTime} more second(s) before submitting.`);
      return;
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    const formKey = formData?.key || "discovery-consultation";

    // Map exact field ids from definition if available
    const fields = formData?.fields || [];
    const findFieldId = (keyName: string) => {
      const found = fields.find((f: any) => f.key?.toLowerCase() === keyName.toLowerCase());
      return found ? found.id : keyName;
    };
    const guestCompositionSummary = `Seniors (65+): ${seniors}, Adults (18-64): ${adults}, Teens (12-17): ${teens}, Kids (<12): ${kids} (Total: ${totalTravelers})`;
    const submissionValues: Record<string, any> = {};

    // Standard field mappings
    const fNameId = findFieldId("full_name") || findFieldId("name") || "f_name";
    const fEmailId = findFieldId("email") || findFieldId("email_address") || "f_email";
    const fPhoneId = findFieldId("phone") || findFieldId("phone_number") || "f_phone";
    const fOccasionId = findFieldId("milestone_occasion") || findFieldId("occasion") || "f_occasion";
    const fDatesId = findFieldId("target_dates") || findFieldId("travel_dates") || "f_dates";
    const fGuestsId = findFieldId("guest_composition") || findFieldId("total_travelers") || "f_guests";
    const fReqsId = findFieldId("special_requirements") || findFieldId("preferences") || "f_requirements";
    submissionValues[fNameId] = fullName;
    submissionValues[fEmailId] = email;
    if (phone) submissionValues[fPhoneId] = phone;
    if (occasion) submissionValues[fOccasionId] = occasion;
    if (travelDates) submissionValues[fDatesId] = travelDates;
    submissionValues[fGuestsId] = guestCompositionSummary;
    if (specialReqs) submissionValues[fReqsId] = specialReqs;
    const hpPayload = formData?.spam?.honeypot?.field ? {
      [formData.spam.honeypot.field]: honeypotVal
    } : undefined;
    try {
      if (client?.pluginRequest) {
        const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
          method: "POST",
          data: {
            values: submissionValues,
            token: formData?.spam?.token || "",
            hp: hpPayload,
            meta: {
              source_url: typeof window !== "undefined" ? window.location.href : "",
              referrer: typeof document !== "undefined" ? document.referrer : ""
            }
          }
        });
        if (res.success) {
          if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
            window.location.assign(res.confirmation.url);
          } else {
            setConfirmationHtml(res.confirmation?.message || "Thank you for sharing your milestone vision. Founders Scott & Michael will review your requirements and be in touch within 24 hours.");
            setSubmitSuccess(true);
          }
        } else if (res.code === "frms_validation_failed") {
          setFieldErrors(res.errors ?? {});
          setErrorMessage(res.message || "Please review the highlighted fields.");
        } else {
          setErrorMessage(res.message || "Unable to submit inquiry. Please try again.");
        }
      } else {
        // Fallback simulation for live UI rendering without REST connection
        setConfirmationHtml("Thank you for sharing your milestone vision. Founders Scott & Michael will review your requirements and be in touch shortly.");
        setSubmitSuccess(true);
      }
    } catch (err: any) {
      setErrorMessage(err?.message || "A network error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    setSubmitSuccess(false);
    setFullName("");
    setEmail("");
    setPhone("");
    setOccasion("");
    setTravelDates("");
    setSpecialReqs("");
    setSeniors(2);
    setAdults(4);
    setTeens(2);
    setKids(0);
    setFetchTimestamp(Date.now());
    setErrorMessage("");
    setFieldErrors({});
  };
  return <section id="discoveryconsultation" data-nav="light" className="relative bg-background text-foreground border-t border-b border-border/60 py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5/12 width) */}
          <div className="lg:col-span-5 flex flex-col space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                  E•Leete Travel Design Studio
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-[1.15]">
                Begin your discovery consultation
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                We design complex multi-generational journeys from $15,000 to
                $50,000+ where generations connect seamlessly. Share your
                family's milestone vision below, and Scott and Michael will
                personally review your requirements.
              </p>
            </div>

            {/* Three Pillar Cards */}
            <div className="space-y-4 pt-2">
              {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return <div key={pillar.title} data-index={i} className="p-5 rounded-lg border border-border/70 bg-card text-card-foreground shadow-xs transition-colors hover:border-primary/40">
                    <div className="flex items-start gap-3.5">
                      <div className="p-2 rounded-md bg-accent text-accent-foreground shrink-0 mt-0.5">
                        <Icon className="size-4" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-sm sm:text-base text-card-foreground">
                          {pillar.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                          {pillar.description}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            {/* Direct Partner Access Box */}
            <div className="p-5 rounded-lg border border-primary/20 bg-accent/40 text-foreground space-y-2">
              <div className="flex items-center gap-2 text-primary font-medium text-sm">
                <Compass className="size-4 shrink-0" />
                <span>Direct Partner Access</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Every itinerary is handled directly by founders Scott &
                Michael. Consultations are scheduled by appointment upon form
                review.
              </p>
            </div>
          </div>

          {/* Right Column (7/12 width) */}
          <div className="lg:col-span-7">
            <Card className="border border-border/80 shadow-md bg-card text-card-foreground">
              <CardHeader className="border-b border-border/60 pb-6 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="font-serif text-2xl sm:text-3xl text-card-foreground">
                    Discovery Questionnaire
                  </CardTitle>
                  <Badge variant="outline" className="border-primary/30 bg-accent/60 text-accent-foreground text-xs py-1 px-2.5 font-medium">
                    Milestone Tier ($15k–$50k+)
                  </Badge>
                </div>
                <CardDescription className="text-sm text-muted-foreground">
                  Please provide your initial travel framework. All
                  submissions are held in strict confidence.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                <AnimatePresence mode="wait">
                  {submitSuccess ? <motion.div key="success" initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -12
                }} className="py-8 px-4 text-center space-y-6">
                      <div className="mx-auto size-14 rounded-full bg-accent flex items-center justify-center text-primary">
                        <CheckCircle2 className="size-8" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-serif text-2xl font-semibold text-card-foreground">
                          Consultation Request Received
                        </h3>
                        <div className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed" dangerouslySetInnerHTML={{
                      __html: confirmationHtml
                    }} />
                      </div>
                      <div className="pt-2">
                        <Button variant="outline" onClick={handleReset} className="gap-2">
                          <RotateCcw className="size-4" />
                          Submit Another Inquiry
                        </Button>
                      </div>
                    </motion.div> : <form onSubmit={handleSubmit} className="space-y-8">
                      {/* Honeypot field (hidden from users) */}
                      {formData?.spam?.honeypot?.field && <div style={{
                    position: "absolute",
                    left: "-9999px",
                    opacity: 0,
                    pointerEvents: "none"
                  }} aria-hidden="true">
                          <input type="text" name={formData.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} />
                        </div>}

                      {errorMessage && <Alert variant="destructive">
                          <AlertCircle className="size-4" />
                          <AlertTitle>Notice</AlertTitle>
                          <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>}

                      {/* Section 01: Contact & Primary Traveler */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 border-b border-border/60 pb-2">
                          <span className="text-xs font-mono font-semibold text-primary">
                            01
                          </span>
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-card-foreground">
                            Contact & Primary Traveler
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor={nameInputId}>
                              Full Name <span className="text-destructive">*</span>
                            </Label>
                            <Input id={nameInputId} type="text" required placeholder="e.g. Eleanor Vance" value={fullName} onChange={e => setFullName(e.target.value)} className="px-3 py-2 bg-background" />
                            {fieldErrors["f_name"] && <p className="text-xs text-destructive">
                                {fieldErrors["f_name"].message}
                              </p>}
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor={emailInputId}>
                              Email Address{" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <Input id={emailInputId} type="email" required placeholder="eleanor@vancefamily.com" value={email} onChange={e => setEmail(e.target.value)} className="px-3 py-2 bg-background" />
                            {fieldErrors["f_email"] && <p className="text-xs text-destructive">
                                {fieldErrors["f_email"].message}
                              </p>}
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor={phoneInputId}>
                              Direct Phone / Mobile
                            </Label>
                            <Input id={phoneInputId} type="tel" placeholder="+1 (555) 019-2834" value={phone} onChange={e => setPhone(e.target.value)} className="px-3 py-2 bg-background" />
                          </div>
                        </div>
                      </div>

                      {/* Section 02: Milestone Occasion & Guest Composition */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 border-b border-border/60 pb-2">
                          <span className="text-xs font-mono font-semibold text-primary">
                            02
                          </span>
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-card-foreground">
                            Milestone Occasion & Guest Composition
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label>Milestone Occasion</Label>
                            <Select value={occasion} onValueChange={setOccasion}>
                              <SelectTrigger className="w-full bg-background">
                                <SelectValue placeholder="Select Occasion Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="70th Birthday">
                                  70th Birthday Celebration
                                </SelectItem>
                                <SelectItem value="50th Anniversary">
                                  50th Golden Anniversary
                                </SelectItem>
                                <SelectItem value="Family Reunion">
                                  Multi-Generational Reunion
                                </SelectItem>
                                <SelectItem value="Other">
                                  Other Bespoke Milestone
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor={datesInputId}>Target Travel Dates</Label>
                            <Input id={datesInputId} type="text" placeholder="e.g. Summer 2026 / October 12–22" value={travelDates} onChange={e => setTravelDates(e.target.value)} className="px-3 py-2 bg-background" />
                          </div>
                        </div>

                        {/* Guest Count Allocation Steppers */}
                        <div className="space-y-2 pt-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                              Guest Count Allocation
                            </Label>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-accent text-accent-foreground">
                              Total Travelers: {totalTravelers}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-lg border border-border/70 bg-background/50">
                            {/* Seniors */}
                            <div className="flex flex-col items-center justify-between p-2 rounded-md bg-card border border-border/60">
                              <span className="text-xs font-medium text-muted-foreground">
                                Seniors (65+)
                              </span>
                              <div className="flex items-center gap-2 mt-2">
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setSeniors(v => Math.max(0, v - 1))}>
                                  <Minus className="size-3" />
                                </Button>
                                <span className="font-mono text-sm font-semibold w-4 text-center">
                                  {seniors}
                                </span>
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setSeniors(v => v + 1)}>
                                  <Plus className="size-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Adults */}
                            <div className="flex flex-col items-center justify-between p-2 rounded-md bg-card border border-border/60">
                              <span className="text-xs font-medium text-muted-foreground">
                                Adults (18–64)
                              </span>
                              <div className="flex items-center gap-2 mt-2">
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setAdults(v => Math.max(0, v - 1))}>
                                  <Minus className="size-3" />
                                </Button>
                                <span className="font-mono text-sm font-semibold w-4 text-center">
                                  {adults}
                                </span>
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setAdults(v => v + 1)}>
                                  <Plus className="size-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Teens */}
                            <div className="flex flex-col items-center justify-between p-2 rounded-md bg-card border border-border/60">
                              <span className="text-xs font-medium text-muted-foreground">
                                Teens (12–17)
                              </span>
                              <div className="flex items-center gap-2 mt-2">
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setTeens(v => Math.max(0, v - 1))}>
                                  <Minus className="size-3" />
                                </Button>
                                <span className="font-mono text-sm font-semibold w-4 text-center">
                                  {teens}
                                </span>
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setTeens(v => v + 1)}>
                                  <Plus className="size-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Kids */}
                            <div className="flex flex-col items-center justify-between p-2 rounded-md bg-card border border-border/60">
                              <span className="text-xs font-medium text-muted-foreground">
                                Kids (&lt;12)
                              </span>
                              <div className="flex items-center gap-2 mt-2">
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setKids(v => Math.max(0, v - 1))}>
                                  <Minus className="size-3" />
                                </Button>
                                <span className="font-mono text-sm font-semibold w-4 text-center">
                                  {kids}
                                </span>
                                <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setKids(v => v + 1)}>
                                  <Plus className="size-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section 03: Tailored Requirements */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 border-b border-border/60 pb-2">
                          <span className="text-xs font-mono font-semibold text-primary">
                            03
                          </span>
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-card-foreground">
                            Tailored Requirements
                          </h4>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={reqsInputId}>
                            Special Accommodations & Preferences
                          </Label>
                          <Textarea id={reqsInputId} rows={3} placeholder="Detail any mobility preferences, private aviation needs, charter aspirations, or milestone celebration ideals..." value={specialReqs} onChange={e => setSpecialReqs(e.target.value)} className="px-3 py-2 bg-background min-h-24" />
                        </div>
                      </div>

                      {/* Submit Actions */}
                      <div className="pt-2 space-y-3">
                        <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium shadow-sm transition-all">
                          {isSubmitting ? <span className="flex items-center gap-2">
                              <Clock className="size-4 animate-spin" />
                              Submitting Consultation Request...
                            </span> : <span>Request Your Discovery Consultation</span>}
                        </Button>
                        <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                          <ShieldCheck className="size-3.5 text-primary" />
                          Discreet, high-touch consultation. No automated spam or shared data.
                        </p>
                      </div>
                    </form>}
                </AnimatePresence>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}